import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  contractualTerms,
  documents,
  limitReviews,
  nominations,
  pipes,
  settings,
  type BrokerDispatch,
  type PipeCardDetail,
  type PipeCardSubjectPerson,
  type PipeEngineRun,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatCpfCnpj,
  formatDateTime,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  parseMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { gruposDeModalidade } from '../policyholders/rotulos';
import { tomadorDoCartao } from './cartao';

/**
 * Paineis da gaveta do cartao que nao e so a cotacao (P-EST-2): o pedido de nomeacao e
 * o de revisao de limite, o tomador com os Grupos Economicos, as pessoas com PEP, o
 * Follow up do cartao sem cotacao, o envio ao corretor, a minuta condicionada da
 * Subscricao e o Historico do motor por tipo de execucao.
 *
 * Cada painel so mostra o que o servidor devolveu e manda a acao para a rota do dono:
 * a decisao do pedido e da propria rota do pedido, com a alcada dela. Ver
 * docs/ADR/0056-esteiras-sujeitos-e-envio.md.
 */

const tiposDeNomeacao: Record<string, string> = {
  pedido: 'Pedido da corretora',
  seguradora: 'Nomeacao pela seguradora',
  remocao: 'Remocao do corretor',
};

const instrumentos: Record<string, string> = {
  primeira_emissao: 'Primeira emissao',
  ccg: 'CCG',
  carta_nomeacao: 'Carta de nomeacao',
};

const tiposDeExecucao = [
  { value: 'credito', label: 'Credito' },
  { value: 'cadastro', label: 'Cadastro' },
  { value: 'pep', label: 'PEP' },
  { value: 'subscricao', label: 'Subscricao' },
  { value: 'resseguro', label: 'Resseguro' },
];

function rotuloDoTipo(valor: string): string {
  return tiposDeExecucao.find((t) => t.value === valor)?.label ?? valor;
}

function documento(valor: string | undefined | null): string {
  return valor ? formatCpfCnpj(valor) : '-';
}

function invalidarCartao(
  queryClient: ReturnType<typeof useQueryClient>,
  cardId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: ['pipe-cards', cardId] });
  void queryClient.invalidateQueries({ queryKey: ['pipes'] });
}

// --- O pedido do cartao: nomeacao e revisao de limite --------------------------------

/**
 * Aba Solicitacao do cartao de nomeacao (Dados da Solicitacao de Nomeacao) e o pedido
 * do cartao Revisao de Limite, com a decisao do pedido.
 *
 * Aprovar, concluir e recusar vao para a rota do pedido, que confere a alcada e encerra
 * o cartao pela decisao. Concluir a revisao com o limite aprovado grava o limite novo em
 * Limites e Taxas, com historico e trilha; quem nao tem a alcada do limite conclui sem
 * mudar o limite.
 */
export function CardSubjectRequest({ cartao }: { cartao: PipeCardDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [decidindo, setDecidindo] = useState<'aprovar' | 'recusar' | null>(null);
  const pedido = cartao.subject;
  const nomeacao = cartao.subject_type === 'nomeacao';

  const baixar = useMutation({
    mutationFn: (documentId: string) => documents.download(documentId),
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const decidir = useMutation({
    mutationFn: async (input: {
      aprovar: boolean;
      motivo: string;
      nota: string;
      limite: Money | null;
    }) => {
      if (!pedido) return;
      if (nomeacao) {
        await nominations.decide(pedido.policyholder.id, cartao.subject_id, {
          decision: input.aprovar ? 'aprovar' : 'recusar',
          ...(input.motivo ? { decline_reason_id: input.motivo } : {}),
          note: input.nota,
        });
        return;
      }
      await limitReviews.decide(cartao.subject_id, {
        decision: input.aprovar ? 'concluir' : 'recusar',
        decline_reason_id: input.motivo || null,
        note: input.nota,
        ...(input.limite ? { approved_limit: input.limite } : {}),
      });
    },
    onSuccess: (_, input) => {
      toast.success(
        nomeacao
          ? input.aprovar
            ? 'Nomeacao aprovada.'
            : 'Nomeacao recusada.'
          : input.aprovar
            ? input.limite
              ? 'Revisao de limite concluida. O limite novo esta em Limites e Taxas.'
              : 'Revisao de limite concluida. O limite nao mudou.'
            : 'Revisao de limite recusada.',
      );
      setDecidindo(null);
      invalidarCartao(queryClient, cartao.id);
      void queryClient.invalidateQueries({ queryKey: ['limit-reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['policyholder'] });
    },
  });

  if (!pedido) {
    return (
      <EmptyState
        icon="file"
        title="Pedido indisponivel"
        description="O pedido deste cartao nao foi encontrado ou nao esta no seu acesso."
      />
    );
  }

  const pendente = pedido.status === 'pendente';
  const podeDecidir = nomeacao
    ? can('tomadores.nomeacao.approve')
    : can('credito.revisao_limite.approve');
  // O valor so aparece para quem tem a alcada do limite. Quem so decide o pedido conclui
  // sem mudar o limite.
  const podeMudarOLimite = !nomeacao && can('tomadores.credito.update');

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Info
          rotulo="Data da solicitacao"
          valor={formatDateTime(pedido.requested_at)}
        />
        <Info
          rotulo="Situacao"
          valor={<StatusBadge kind="pedidoCartao" value={pedido.status} />}
        />
        <Info
          rotulo="Solicitado por"
          valor={pedido.requested_label || '-'}
          extra={
            pedido.requested_by_broker ? 'Pedido da corretora' : 'Pedido da seguradora'
          }
        />
        <Info rotulo="Tomador" valor={pedido.policyholder.label} />
        <Info rotulo="CNPJ Tomador" valor={documento(pedido.policyholder.document)} />
        <Info
          rotulo={nomeacao ? 'Corretora Solicitante' : 'Corretora'}
          valor={pedido.broker?.label ?? '-'}
        />
        <Info rotulo="CNPJ Corretora" valor={documento(pedido.broker?.document)} />
        {nomeacao ? (
          <>
            <Info
              rotulo="Tipo do pedido"
              valor={tiposDeNomeacao[pedido.kind] ?? pedido.kind}
            />
            <Info rotulo="Validade" valor={formatCivilDate(pedido.valid_until)} />
            <Info
              rotulo="Instrumento"
              valor={instrumentos[pedido.instrument] ?? (pedido.instrument || '-')}
            />
          </>
        ) : (
          <>
            <Info
              rotulo="Grupo"
              valor={gruposDeModalidade[pedido.modality_group] ?? pedido.modality_group}
            />
            <Info
              rotulo="Limite pretendido"
              valor={formatMoney(pedido.requested_amount)}
            />
            <Info
              rotulo="Limite no pedido"
              valor={
                pedido.current_limit
                  ? formatMoney(pedido.current_limit)
                  : 'Sem limite aprovado'
              }
            />
            <Info
              rotulo="Limite na conclusao"
              valor={pedido.reviewed_limit ? formatMoney(pedido.reviewed_limit) : '-'}
            />
          </>
        )}
      </div>

      {pedido.note ? (
        <Texto rotulo={nomeacao ? 'Observacao do pedido' : 'Motivo do pedido'}>
          {pedido.note}
        </Texto>
      ) : null}

      {pedido.decided_at ? (
        <Texto rotulo="Decisao">
          {`${pedido.decided_label || 'Sistema'} - ${formatDateTime(pedido.decided_at)}`}
          {pedido.decline_reason ? `\nMotivo: ${pedido.decline_reason}` : ''}
          {pedido.decision_note ? `\n${pedido.decision_note}` : ''}
        </Texto>
      ) : null}

      {!nomeacao ? (
        <p className="text-2xs text-ink-500">
          {podeMudarOLimite
            ? 'Ao concluir, informe o limite aprovado: ele vai para Limites e Taxas do tomador, com historico.'
            : 'Mudar o limite pede a alcada do credito. Sem ela, concluir o pedido nao muda o limite.'}
        </p>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        {nomeacao && pedido.letter_document_id && can('documentos.documento.view') ? (
          <Button
            variant="secondary"
            loading={baixar.isPending}
            onClick={() => baixar.mutate(pedido.letter_document_id ?? '')}
          >
            Baixar carta de nomeacao
          </Button>
        ) : null}
        {pendente && podeDecidir ? (
          <>
            <Button variant="danger" onClick={() => setDecidindo('recusar')}>
              Recusar
            </Button>
            <Button onClick={() => setDecidindo('aprovar')}>
              {nomeacao ? 'Aprovar' : 'Concluir'}
            </Button>
          </>
        ) : null}
      </div>

      <CardSubjectDecisionDialog
        key={decidindo ?? 'fechado'}
        acao={decidindo}
        escopo={nomeacao ? 'nomeacao' : 'credito'}
        limitePretendido={
          podeMudarOLimite && decidindo === 'aprovar' ? pedido.requested_amount : null
        }
        titulo={
          decidindo === 'recusar'
            ? nomeacao
              ? 'Recusar a nomeacao'
              : 'Recusar a revisao de limite'
            : nomeacao
              ? 'Aprovar a nomeacao'
              : 'Concluir a revisao de limite'
        }
        pendente={decidir.isPending}
        erro={decidir.error?.message ?? ''}
        onClose={() => {
          setDecidindo(null);
          decidir.reset();
        }}
        onConfirm={(motivo, nota, limite) =>
          decidir.mutate({ aprovar: decidindo === 'aprovar', motivo, nota, limite })
        }
      />
    </div>
  );
}

/** Confirmacao da decisao do pedido: a recusa pede o motivo ou a justificativa. */
function CardSubjectDecisionDialog({
  acao,
  escopo,
  limitePretendido,
  titulo,
  pendente,
  erro,
  onClose,
  onConfirm,
}: {
  acao: 'aprovar' | 'recusar' | null;
  escopo: 'nomeacao' | 'credito';
  /** Com valor, a confirmacao pede o limite aprovado, comecando pelo pretendido. */
  limitePretendido: Money | null;
  titulo: string;
  pendente: boolean;
  erro: string;
  onClose: () => void;
  onConfirm: (motivo: string, nota: string, limite: Money | null) => void;
}) {
  const { can } = useAuth();
  const [motivo, setMotivo] = useState('');
  const [nota, setNota] = useState('');
  const [limite, setLimite] = useState(moneyToInput(limitePretendido));
  const recusa = acao === 'recusar';

  const motivos = useQuery({
    queryKey: ['decline-reasons', escopo],
    queryFn: ({ signal }) => settings.declineReasons({ scope: escopo }, signal),
    enabled: recusa && can('configuracoes.motivo.view'),
    staleTime: 5 * 60_000,
  });

  const limiteAprovado = limitePretendido ? parseMoney(limite) : null;
  const falta =
    (recusa && motivo === '' && nota.trim() === '') ||
    (limitePretendido !== null && limiteAprovado === null);

  function fechar() {
    setMotivo('');
    setNota('');
    onClose();
  }

  return (
    <Modal
      open={acao !== null}
      onClose={fechar}
      title={titulo}
      description="A decisao fica no pedido e no historico do cartao, com quem decidiu e quando."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            variant={recusa ? 'danger' : 'primary'}
            loading={pendente}
            disabled={falta}
            onClick={() => onConfirm(motivo, nota.trim(), limiteAprovado)}
          >
            Confirmar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {erro ? (
          <Alert tone="danger" title="Nao foi possivel decidir o pedido">
            {erro}
          </Alert>
        ) : null}
        {recusa && (motivos.data ?? []).length > 0 ? (
          <Select
            label="Motivo padronizado"
            placeholder="Sem motivo padronizado"
            options={(motivos.data ?? [])
              .filter((m) => m.active)
              .map((m) => ({ value: m.id, label: m.label }))}
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
          />
        ) : null}
        {limitePretendido ? (
          <Field
            label="Limite aprovado (R$)"
            inputMode="decimal"
            required
            value={limite}
            error={moneyInputError(limite)}
            hint="Comeca pelo limite pretendido. Fica em Limites e Taxas, como aprovado por voce."
            onChange={(event) => setLimite(event.target.value)}
          />
        ) : null}
        <Textarea
          label="Justificativa"
          value={nota}
          maxLength={2000}
          required={recusa && motivo === ''}
          hint={
            recusa
              ? 'A recusa exige o motivo padronizado ou a justificativa.'
              : 'Opcional.'
          }
          onChange={(event) => setNota(event.target.value)}
        />
      </div>
    </Modal>
  );
}

// --- O tomador: Grupos Economicos e pessoas com PEP ----------------------------------

/**
 * Quadro Grupos Economicos da esteira Cadastro e do cartao de tomador, com o aviso do
 * tomador sem grupo. Adicionar e atualizar o grupo ficam na secao Grupo Economico do
 * tomador, que confere a permissao da ficha.
 */
export function CardSubjectGroups({ cartao }: { cartao: PipeCardDetail }) {
  const { can } = useAuth();
  const grupos = cartao.economic_groups;
  if (!grupos) return null;
  const tomador = tomadorDoCartao(cartao);
  const destino = `/tomadores/${tomador}?secao=grupo`;
  return (
    <section
      aria-label="Grupos Economicos"
      className="flex flex-col gap-3 rounded-md border border-line p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
          Grupos Economicos
        </p>
        {grupos.available && tomador && can('tomadores.ficha.view') ? (
          <div className="flex flex-wrap gap-2">
            <LinkDeAcao to={destino}>Adicionar novo grupo</LinkDeAcao>
            {grupos.items.length > 0 ? (
              <LinkDeAcao to={destino}>Atualizar Grupo economico</LinkDeAcao>
            ) : null}
          </div>
        ) : null}
      </div>
      {!grupos.available ? (
        <p className="text-sm text-ink-600">{grupos.reason}</p>
      ) : grupos.items.length === 0 ? (
        <Alert tone="warning" title="Tomador sem grupo economico">
          O tomador nao tem grupo economico cadastrado.
        </Alert>
      ) : (
        <DataTable
          columns={[
            { key: 'nome', header: 'Grupo', render: (g) => g.name || '-' },
            { key: 'cnpj', header: 'CNPJ', render: (g) => documento(g.document) },
            {
              key: 'participacao',
              header: 'Participacao',
              align: 'right',
              render: (g) => formatRate(g.participation_bps),
            },
            {
              key: 'limite',
              header: 'Limite do grupo',
              align: 'right',
              hideBelow: 'md',
              render: (g) => (g.group_limit ? formatMoney(g.group_limit) : '-'),
            },
          ]}
          items={grupos.items}
          keyOf={(g) => g.id}
        />
      )}
    </section>
  );
}

/**
 * Socios, Administradores e Representantes da esteira PEP, com a marca de PEP de cada
 * pessoa. A alcada do PEP (GOV-P10) e regra pendente: o quadro so mostra.
 */
export function CardSubjectPeople({ cartao }: { cartao: PipeCardDetail }) {
  const pessoas = cartao.pep_people;
  if (!pessoas) return null;
  if (!pessoas.available) {
    return (
      <section
        aria-label="Pessoas com PEP"
        className="rounded-md border border-line p-3"
      >
        <p className="text-sm text-ink-600">{pessoas.reason}</p>
      </section>
    );
  }
  return (
    <section aria-label="Pessoas com PEP" className="flex flex-col gap-3">
      <p className="text-xs text-ink-600">
        {pessoas.pep_count === 0
          ? 'Nenhuma pessoa do quadro do tomador esta marcada como PEP.'
          : `${pessoas.pep_count} pessoa(s) do quadro do tomador marcada(s) como PEP.`}
      </p>
      <TabelaDePessoas titulo="Socios" itens={pessoas.partners} comParticipacao />
      <TabelaDePessoas titulo="Administradores" itens={pessoas.administrators} />
      <TabelaDePessoas titulo="Representantes" itens={pessoas.representatives} />
    </section>
  );
}

function TabelaDePessoas({
  titulo,
  itens,
  comParticipacao = false,
}: {
  titulo: string;
  itens: PipeCardSubjectPerson[];
  comParticipacao?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {titulo}
      </p>
      <DataTable
        columns={[
          { key: 'nome', header: 'Nome', render: (p: PipeCardSubjectPerson) => p.name },
          {
            key: 'documento',
            header: 'CPF/CNPJ',
            render: (p) => documento(p.document),
          },
          ...(comParticipacao
            ? [
                {
                  key: 'participacao',
                  header: 'Participacao',
                  align: 'right' as const,
                  render: (p: PipeCardSubjectPerson) =>
                    p.participation_bps === null
                      ? '-'
                      : formatRate(p.participation_bps),
                },
              ]
            : []),
          {
            key: 'pep',
            header: 'PEP',
            render: (p) =>
              p.is_pep ? (
                <Badge tone="danger">PEP</Badge>
              ) : (
                <Badge tone="neutral">Nao</Badge>
              ),
          },
        ]}
        items={itens}
        keyOf={(p) => p.id}
        empty={<p className="px-3 py-4 text-sm text-ink-500">Nenhum cadastrado.</p>}
      />
    </div>
  );
}

/** Dados do tomador do cartao de tomador (Cadastro), com o link para a ficha. */
export function CardSubjectPolicyholder({ cartao }: { cartao: PipeCardDetail }) {
  const { can } = useAuth();
  const sujeito = cartao.subject;
  if (!sujeito) {
    return (
      <EmptyState
        icon="users"
        title="Tomador indisponivel"
        description="O tomador deste cartao nao foi encontrado ou nao esta no seu acesso."
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Info rotulo="Tomador" valor={sujeito.policyholder.label} />
        <Info rotulo="CNPJ Tomador" valor={documento(sujeito.policyholder.document)} />
        <Info rotulo="Corretora" valor={sujeito.broker?.label ?? '-'} />
        <Info rotulo="Tipo do cartao" valor={cartao.title || cartao.card_type} />
        <Info rotulo="Aberto em" valor={formatDateTime(cartao.entered_pipe_at)} />
      </div>
      {can('tomadores.tomador.view') ? (
        <div className="flex justify-end">
          <LinkDeAcao to={`/tomadores/${sujeito.policyholder.id}`}>
            Visualizar cadastro completo do tomador
          </LinkDeAcao>
        </div>
      ) : null}
      <CardSubjectGroups cartao={cartao} />
    </div>
  );
}

// --- Follow up do cartao sem cotacao e envio ao corretor -----------------------------

/**
 * Follow up do cartao que nao e de cotacao (tomador, revisao de limite): os registros
 * do cartao, o registro novo e o "Enviar para o corretor", do comentario novo ou dos
 * registros escolhidos.
 */
export function CardSubjectFollowUp({ cartao }: { cartao: PipeCardDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [texto, setTexto] = useState('');
  const [escolhidos, setEscolhidos] = useState<string[]>([]);

  const consulta = useQuery({
    queryKey: ['pipe-cards', cartao.id, 'comments'],
    queryFn: ({ signal }) => pipes.cardComments(cartao.id, signal),
  });

  const registrar = useMutation({
    mutationFn: () => pipes.addCardComment(cartao.id, { body: texto.trim() }),
    onSuccess: () => {
      toast.success('Registro gravado no Follow up.');
      setTexto('');
      void queryClient.invalidateQueries({ queryKey: ['pipe-cards', cartao.id] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const enviar = useMutation({
    mutationFn: (input: { body?: string; comment_ids?: string[] }) =>
      pipes.sendToBroker(cartao.id, input),
    onSuccess: () => {
      toast.success('Enviado para o corretor.');
      setTexto('');
      setEscolhidos([]);
      invalidarCartao(queryClient, cartao.id);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // O cartao encerrado nao recebe registro nem envio novo: o servidor responde 409.
  const podeEscrever = can('esteiras.cartao.update') && !cartao.is_closed;
  // O cartao de autocadastro nao tem corretora a quem enviar: ela so existe depois da
  // aprovacao, que encerra o cartao (R5-M15). O servidor tambem recusa com 409.
  const podeEnviar =
    can('esteiras.envio.create') &&
    !cartao.is_closed &&
    cartao.subject_type !== 'autocadastro_corretora';
  const registros = [...(consulta.data ?? [])].reverse();

  return (
    <div className="flex flex-col gap-4">
      {podeEscrever || podeEnviar ? (
        <div className="flex flex-col gap-2">
          <Textarea
            label="Novo registro"
            value={texto}
            maxLength={4000}
            hint="Fica no Follow up do cartao. Enviar para o corretor grava o registro ja marcado como enviado."
            onChange={(event) => setTexto(event.target.value)}
          />
          <div className="flex flex-wrap justify-end gap-2">
            {podeEscrever ? (
              <Button
                variant="secondary"
                disabled={texto.trim() === ''}
                loading={registrar.isPending}
                onClick={() => registrar.mutate()}
              >
                Registrar
              </Button>
            ) : null}
            {podeEnviar ? (
              <Button
                disabled={texto.trim() === ''}
                loading={enviar.isPending}
                onClick={() => enviar.mutate({ body: texto.trim() })}
              >
                Enviar para o corretor
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {consulta.isPending ? (
        <SkeletonText lines={3} />
      ) : consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o Follow up">
          {consulta.error.message}
        </Alert>
      ) : registros.length === 0 ? (
        <EmptyState
          icon="clock"
          title="Sem registros"
          description="O Follow up deste cartao ainda nao tem registros."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
          {registros.map((r) => (
            <li key={r.id} className="flex gap-2 px-3 py-2.5">
              {podeEnviar && !r.sent_to_broker_at ? (
                <input
                  type="checkbox"
                  aria-label={`Escolher o registro de ${formatDateTime(r.created_at)}`}
                  checked={escolhidos.includes(r.id)}
                  onChange={(event) =>
                    setEscolhidos((atual) =>
                      event.target.checked
                        ? [...atual, r.id]
                        : atual.filter((x) => x !== r.id),
                    )
                  }
                  className="mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
                />
              ) : null}
              <div className="flex min-w-0 flex-col gap-1">
                {r.title ? (
                  <p className="text-xs font-semibold text-ink-900">{r.title}</p>
                ) : null}
                <p className="whitespace-pre-line text-sm text-ink-800">{r.body}</p>
                <p className="flex flex-wrap items-center gap-1.5 text-2xs text-ink-500">
                  {r.author_label || 'Sistema'} - {formatDateTime(r.created_at)}
                  {r.sent_to_broker_at ? (
                    <Badge tone="success">
                      Enviado ao corretor em {formatDateTime(r.sent_to_broker_at)}
                    </Badge>
                  ) : null}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {podeEnviar && escolhidos.length > 0 ? (
        <div className="flex justify-end">
          <Button
            loading={enviar.isPending}
            onClick={() => enviar.mutate({ comment_ids: escolhidos })}
          >
            Enviar {escolhidos.length} registro(s) para o corretor
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Enviar para o corretor" do Follow up da cotacao: o comentario vai para a conversa da
 * cotacao ja marcado como enviado, e a corretora o ve no portal.
 */
export function BrokerDispatchFollowUp({ cartao }: { cartao: PipeCardDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [texto, setTexto] = useState('');

  const enviar = useMutation({
    mutationFn: () => pipes.sendToBroker(cartao.id, { body: texto.trim() }),
    onSuccess: () => {
      toast.success('Enviado para o corretor.');
      setTexto('');
      invalidarCartao(queryClient, cartao.id);
      void queryClient.invalidateQueries({ queryKey: ['demands'] });
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!can('esteiras.envio.create') || cartao.is_closed) return null;
  return (
    <section
      aria-label="Enviar para o corretor"
      className="flex flex-col gap-2 rounded-md border border-line p-3"
    >
      <Textarea
        label="Mensagem ao corretor"
        value={texto}
        maxLength={4000}
        hint="Entra no Follow up da cotacao marcada como enviada, com quem enviou e quando, e aparece para a corretora no portal."
        onChange={(event) => setTexto(event.target.value)}
      />
      <div className="flex justify-end">
        <Button
          disabled={texto.trim() === ''}
          loading={enviar.isPending}
          onClick={() => enviar.mutate()}
        >
          Enviar para o corretor
        </Button>
      </div>
    </section>
  );
}

/**
 * "Enviar para o corretor" dos Documentos solicitados: manda a lista dos documentos
 * pendentes do objeto do cartao.
 */
export function BrokerDispatchDocuments({ cartao }: { cartao: PipeCardDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [nota, setNota] = useState('');

  const enviar = useMutation({
    mutationFn: () =>
      pipes.sendDocumentRequests(cartao.id, nota.trim() ? { note: nota.trim() } : {}),
    onSuccess: (envio) => {
      toast.success(
        `${envio.document_ids.length} documento(s) pendente(s) enviado(s) para o corretor.`,
      );
      setNota('');
      invalidarCartao(queryClient, cartao.id);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!can('esteiras.envio.create') || cartao.is_closed) return null;
  return (
    <section
      aria-label="Enviar os documentos para o corretor"
      className="flex flex-col gap-2 rounded-md border border-line p-3"
    >
      <Textarea
        label="Comentario do envio"
        value={nota}
        maxLength={2000}
        hint="Opcional. O envio leva a lista dos documentos ainda pendentes."
        onChange={(event) => setNota(event.target.value)}
      />
      <div className="flex justify-end">
        <Button loading={enviar.isPending} onClick={() => enviar.mutate()}>
          Enviar para o corretor
        </Button>
      </div>
    </section>
  );
}

/** Lista "Envios ao corretor" do cartao: o que foi enviado, por quem e quando. */
export function BrokerDispatchList({ envios }: { envios: BrokerDispatch[] }) {
  if (envios.length === 0) return null;
  return (
    <section aria-label="Envios ao corretor" className="flex flex-col gap-1.5">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        Envios ao corretor
      </p>
      <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
        {[...envios].reverse().map((envio) => (
          <li key={envio.id} className="flex flex-col gap-1 px-3 py-2.5">
            <p className="flex flex-wrap items-center gap-1.5 text-xs">
              <Badge tone="brand">
                {envio.kind === 'documentos' ? 'Documentos solicitados' : 'Follow up'}
              </Badge>
              <span className="font-medium text-ink-800">{envio.broker.name}</span>
            </p>
            <p className="line-clamp-3 whitespace-pre-line text-sm text-ink-700">
              {envio.message}
            </p>
            <p className="text-2xs text-ink-500">
              {envio.sent_label || 'Sistema'} - {formatDateTime(envio.sent_at)}. Aviso
              por e-mail: pendente de regra da seguradora.
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Subscricao: minuta condicionada -------------------------------------------------

/**
 * Caixa do Follow up da Subscricao: "Disponibilizar a Minuta para download pelo
 * corretor, antes da aprovacao da cotacao - liberacao condicionada.". Grava a marca da
 * cotacao (P-COT-2), que ainda nao libera nada (COT-200).
 */
export function ConditionalDraftToggle({ operationId }: { operationId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: ['operations', operationId, 'contractual-terms'],
    queryFn: ({ signal }) => contractualTerms.get(operationId, signal),
    enabled: can('operacoes.operacao.view'),
  });
  const gravar = useMutation({
    mutationFn: (ligada: boolean) =>
      contractualTerms.setConditionalDraft(operationId, ligada),
    onSuccess: (termos) => {
      queryClient.setQueryData(
        ['operations', operationId, 'contractual-terms'],
        termos,
      );
      toast.success('Marca da minuta condicionada gravada.');
    },
    onError: (error: Error) => toast.error(error.message),
  });
  if (!consulta.data) return null;
  const pode =
    consulta.data.editable &&
    can('operacoes.operacao.update') &&
    can('operacoes.cotacao_textos.update');
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-line p-3 text-sm text-ink-700">
      <input
        type="checkbox"
        checked={consulta.data.conditional_draft_enabled}
        disabled={!pode || gravar.isPending}
        onChange={(event) => gravar.mutate(event.target.checked)}
        className="mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
      />
      <span>
        Disponibilizar a Minuta para download pelo corretor, antes da aprovacao da
        cotacao - liberacao condicionada.
        <span className="block text-2xs text-ink-500">
          Gravado na cotacao, sem efeito: o que a liberacao condicionada faz ainda nao
          tem fonte (COT-200).
        </span>
      </span>
    </label>
  );
}

// --- Historico do motor por tipo de execucao -----------------------------------------

/**
 * Aba Historico do motor da gaveta, por tipo de execucao (credito_engine_runs.run_kind).
 * A execucao anterior ao tipo ganhou "credito" na migration, sem mudar o resto da linha.
 */
export function EngineLaneHistory({ operationId }: { operationId: string }) {
  const { can } = useAuth();
  const [tipo, setTipo] = useState('');
  const pode = can('credito.motor.view');
  const consulta = useQuery({
    queryKey: ['operations', operationId, 'engine-runs', tipo],
    queryFn: ({ signal }) => pipes.engineRuns(operationId, tipo, signal),
    enabled: pode,
  });

  if (!pode) {
    return (
      <p className="text-sm text-ink-500">
        O historico do motor pede a permissao de ver o motor de credito.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="max-w-xs">
        <Select
          label="Tipo de execucao"
          placeholder="Todos"
          options={tiposDeExecucao}
          value={tipo}
          onChange={(event) => setTipo(event.target.value)}
        />
      </div>
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o historico do motor">
          {consulta.error.message}
        </Alert>
      ) : (
        <DataTable
          columns={[
            {
              key: 'data',
              header: 'Executada em',
              render: (r: PipeEngineRun) => (
                <span className="flex flex-wrap items-center gap-1.5">
                  {formatDateTime(r.created_at)}
                  {r.is_last ? <Badge tone="info">Ultima</Badge> : null}
                </span>
              ),
            },
            { key: 'tipo', header: 'Tipo', render: (r) => rotuloDoTipo(r.run_kind) },
            { key: 'resultado', header: 'Resultado', render: (r) => r.final_result },
            {
              key: 'raias',
              header: 'Raias',
              hideBelow: 'md',
              render: (r) =>
                r.lanes.length === 0
                  ? '-'
                  : r.lanes.map((l) => `${l.lane} (${l.rules.join(', ')})`).join('; '),
            },
            {
              key: 'pendencias',
              header: 'Pendencias',
              align: 'right',
              render: (r) => String(r.pendency_count),
            },
            {
              key: 'quem',
              header: 'Solicitado por',
              hideBelow: 'lg',
              render: (r) => r.requested_label || '-',
            },
          ]}
          items={consulta.data ?? []}
          keyOf={(r) => r.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="chart"
              title="Sem execucoes"
              description={
                tipo
                  ? 'Nenhuma execucao deste tipo para a cotacao.'
                  : 'O motor ainda nao foi executado para esta cotacao.'
              }
            />
          }
        />
      )}
    </div>
  );
}

// --- Pecas de apresentacao ------------------------------------------------------------

function Info({
  rotulo,
  valor,
  extra,
}: {
  rotulo: string;
  valor: ReactNode;
  extra?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </p>
      <div className="mt-0.5 truncate text-sm font-medium text-ink-900">{valor}</div>
      {extra ? <p className="text-2xs text-ink-500">{extra}</p> : null}
    </div>
  );
}

function Texto({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="rounded-md border border-line p-3">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </p>
      <p className="mt-1 whitespace-pre-line text-sm text-ink-700">{children}</p>
    </div>
  );
}

function LinkDeAcao({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex h-8 items-center rounded-md border border-line-strong bg-surface px-3 text-xs font-medium text-ink-700 hover:bg-ink-50"
    >
      {children}
    </Link>
  );
}
